// Product recognition replay runs and their analysis.
import {
  RecognitionReplayAnalysisRequest,
  RecognitionReplayAnalysisResponse,
  RecognitionReplayCancelRequest,
  RecognitionReplayCreateRunRequest,
  RecognitionReplayPopulationPreview,
  RecognitionReplayPopulationRequest,
  RecognitionReplayRunListResponse,
  RecognitionReplayRunResponse,
  RecognitionReplayRunStatus,
} from '../../types/recognitionReplay';
import { forgeRequest, forgeVoidRequest } from '../forgeRequest';
import { FORGE_PR_REPLAY_API_URL } from './endpoints';

/** Dry-run sample count/mix for a Recognition Replay population, before a run is created. */
export async function previewRecognitionReplayPopulation(
  token: string,
  request: RecognitionReplayPopulationRequest
): Promise<RecognitionReplayPopulationPreview> {
  return forgeRequest<RecognitionReplayPopulationPreview>(
    token,
    `${FORGE_PR_REPLAY_API_URL}/population/preview`,
    'Error previewing Recognition Replay population',
    { method: 'POST', body: request }
  );
}

/** Freezes the population and creates a PENDING Recognition Replay run. */
export async function createRecognitionReplayRun(
  token: string,
  request: RecognitionReplayCreateRunRequest
): Promise<RecognitionReplayRunResponse> {
  return forgeRequest<RecognitionReplayRunResponse>(
    token,
    FORGE_PR_REPLAY_API_URL,
    'Error creating Recognition Replay run',
    { method: 'POST', body: request }
  );
}

/** Transitions a PENDING run to RUNNING; execution proceeds asynchronously. */
export async function startRecognitionReplayRun(token: string, runId: string): Promise<void> {
  return forgeVoidRequest(
    token,
    `${FORGE_PR_REPLAY_API_URL}/${encodeURIComponent(runId)}/start`,
    'Error starting Recognition Replay run',
    { method: 'POST' }
  );
}

/** Polls one Recognition Replay run for its current status/progress. */
export async function fetchRecognitionReplayRun(
  token: string,
  runId: string
): Promise<RecognitionReplayRunResponse> {
  return forgeRequest<RecognitionReplayRunResponse>(
    token,
    `${FORGE_PR_REPLAY_API_URL}/${encodeURIComponent(runId)}`,
    'Error fetching Recognition Replay run'
  );
}

/** Lists Recognition Replay runs, optionally filtered by status, newest first. */
export async function fetchRecognitionReplayRuns(
  token: string,
  params?: { status?: RecognitionReplayRunStatus; skip?: number; limit?: number }
): Promise<RecognitionReplayRunListResponse> {
  const query = new URLSearchParams();
  if (params?.status) query.set('status', params.status);
  if (params?.skip !== undefined) query.set('skip', String(params.skip));
  if (params?.limit !== undefined) query.set('limit', String(params.limit));
  const search = query.toString();
  return forgeRequest<RecognitionReplayRunListResponse>(
    token,
    `${FORGE_PR_REPLAY_API_URL}${search ? `?${search}` : ''}`,
    'Error fetching Recognition Replay runs'
  );
}

/** Cooperatively cancels a Recognition Replay run; it stops between images. */
export async function cancelRecognitionReplayRun(
  token: string,
  runId: string,
  request?: RecognitionReplayCancelRequest
): Promise<void> {
  return forgeVoidRequest(
    token,
    `${FORGE_PR_REPLAY_API_URL}/${encodeURIComponent(runId)}/cancel`,
    'Error cancelling Recognition Replay run',
    { method: 'POST', body: request ?? {} }
  );
}

/** Sets or clears a run's free-text decision note. `decisionNote: null` clears it. */
export async function patchRecognitionReplayDecisionNote(
  token: string,
  runId: string,
  decisionNote: string | null
): Promise<RecognitionReplayRunResponse> {
  return forgeRequest<RecognitionReplayRunResponse>(
    token,
    `${FORGE_PR_REPLAY_API_URL}/${encodeURIComponent(runId)}/decision-note`,
    "Error saving Recognition Replay run's design note",
    { method: 'PATCH', body: { decisionNote } }
  );
}

/** Scores one completed run's frozen results under one or more candidate parameter configs. */
export async function analyzeRecognitionReplayRun(
  token: string,
  runId: string,
  request: RecognitionReplayAnalysisRequest
): Promise<RecognitionReplayAnalysisResponse> {
  return forgeRequest<RecognitionReplayAnalysisResponse>(
    token,
    `${FORGE_PR_REPLAY_API_URL}/${encodeURIComponent(runId)}/analysis`,
    'Error analyzing Recognition Replay run',
    { method: 'POST', body: request }
  );
}
